import type { AccessToken } from '@okta/okta-auth-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getIdleDurationMs, recordActivity } from '@/utils/activityTracker';
import { getOktaAuth, getValidOktaAccessToken } from '@/utils/oktaAuth';

/** Show warning after 20 min of inactivity */
const WARNING_THRESHOLD_SECONDS = 20 * 60;
/** Auto-logout after 25 min of inactivity (warning displays for up to 5 min) */
const LOGOUT_THRESHOLD_SECONDS = 25 * 60;
/** Silently renew token when TTL falls below this threshold */
const TOKEN_RENEWAL_THRESHOLD_SECONDS = 5 * 60;
const POLL_INTERVAL_MS = 10_000;

interface InactivityMonitorState {
	warningOpen: boolean;
	secondsRemaining: number;
	idleSeconds: number;
	tokenTTL: number | null;
}

async function getTokenExpiresIn(): Promise<number | null> {
	const oktaAuth = getOktaAuth();
	if (!oktaAuth) return null;
	try {
		const token = (await oktaAuth.tokenManager.get('accessToken')) as
			| AccessToken
			| undefined;
		if (!token) return null;
		const now = Math.floor(Date.now() / 1000);
		return token.expiresAt - now;
	} catch {
		return null;
	}
}

export function useInactivityMonitor(): {
	warningOpen: boolean;
	secondsRemaining: number;
	idleSeconds: number;
	tokenTTL: number | null;
	handleStaySignedIn: () => void;
	handleSignOut: () => void;
} {
	const [state, setState] = useState<InactivityMonitorState>({
		warningOpen: false,
		secondsRemaining: LOGOUT_THRESHOLD_SECONDS - WARNING_THRESHOLD_SECONDS,
		idleSeconds: 0,
		tokenTTL: null,
	});

	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const renewingRef = useRef(false);

	useEffect(() => {
		const poll = async () => {
			const expiresIn = await getTokenExpiresIn();
			const idleMs = getIdleDurationMs();
			const idleSec = Math.round(idleMs / 1000);

			console.debug(
				`[InactivityMonitor] idle=${idleSec}s | tokenTTL=${expiresIn ?? 'none'}s`,
			);

			// No Okta session
			if (expiresIn === null) {
				setState((prev) =>
					prev.warningOpen
						? {
								warningOpen: false,
								secondsRemaining: 0,
								idleSeconds: idleSec,
								tokenTTL: null,
							}
						: { ...prev, idleSeconds: idleSec, tokenTTL: null },
				);
				return;
			}

			// Token actually expired (TTL <= 0)
			if (expiresIn <= 0) {
				if (pollRef.current) {
					clearInterval(pollRef.current);
					pollRef.current = null;
				}
				setState({
					warningOpen: false,
					secondsRemaining: 0,
					idleSeconds: idleSec,
					tokenTTL: expiresIn,
				});
				window.dispatchEvent(new CustomEvent('okta-token-expired'));
				return;
			}

			// Inactivity auto-logout (>= 25 min idle)
			if (idleSec >= LOGOUT_THRESHOLD_SECONDS) {
				if (pollRef.current) {
					clearInterval(pollRef.current);
					pollRef.current = null;
				}
				setState({
					warningOpen: false,
					secondsRemaining: 0,
					idleSeconds: idleSec,
					tokenTTL: expiresIn,
				});
				window.dispatchEvent(new CustomEvent('inactivity-logout'));
				return;
			}

			// Inactivity warning (>= 20 min idle)
			if (idleSec >= WARNING_THRESHOLD_SECONDS) {
				const remaining = LOGOUT_THRESHOLD_SECONDS - idleSec;
				setState({
					warningOpen: true,
					secondsRemaining: Math.max(1, remaining),
					idleSeconds: idleSec,
					tokenTTL: expiresIn,
				});
				return;
			}

			// User is active — close warning if open
			setState((prev) =>
				prev.warningOpen
					? prev
					: { ...prev, idleSeconds: idleSec, tokenTTL: expiresIn },
			);

			getValidOktaAccessToken().then(() => {
				console.debug('[Activity Monitor]: Check Valid Okta Token');
			});
			// Proactively renew token if close to expiry while user is active
			// const oktaAuth = getOktaAuth();
			// if (
			// 	expiresIn <= TOKEN_RENEWAL_THRESHOLD_SECONDS &&
			// 	oktaAuth &&
			// 	!renewingRef.current
			// ) {
			// 	renewingRef.current = true;
			// 	oktaAuth.tokenManager
			// 		.renew('accessToken')
			// 		.then(() => {
			// 			window.dispatchEvent(new CustomEvent('okta-token-renewed'));
			// 		})
			// 		.catch(() => {
			// 			window.dispatchEvent(new CustomEvent('okta-token-expired'));
			// 		})
			// 		.finally(() => {
			// 			renewingRef.current = false;
			// 		});
			// 	recordActivity();
			// }
		};

		poll();
		pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

		return () => {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		};
	}, []);

	const handleStaySignedIn = useCallback(() => {
		setState((prev) => ({
			...prev,
			warningOpen: false,
			secondsRemaining: LOGOUT_THRESHOLD_SECONDS - WARNING_THRESHOLD_SECONDS,
			idleSeconds: 0,
		}));
		recordActivity();
		const oktaAuth = getOktaAuth();
		if (oktaAuth) {
			oktaAuth.tokenManager
				.renew('accessToken')
				.then(() => {
					window.dispatchEvent(new CustomEvent('okta-token-renewed'));
				})
				.catch((err) => {
					console.error('Refresh token renewal failed on stay-signed-in:', err);
					window.dispatchEvent(new CustomEvent('okta-token-expired'));
				});
		}
	}, []);

	const handleSignOut = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
		setState((prev) => ({ ...prev, warningOpen: false, secondsRemaining: 0 }));
		window.dispatchEvent(new CustomEvent('inactivity-logout'));
	}, []);

	return {
		warningOpen: state.warningOpen,
		secondsRemaining: state.secondsRemaining,
		idleSeconds: state.idleSeconds,
		tokenTTL: state.tokenTTL,
		handleStaySignedIn,
		handleSignOut,
	};
}
